import { ALL_MODULES, getTranslatedText } from '@nile/components';
import { WORKLOG_ITEM_STATE } from '@nile/components/src/Constants/worklog';
import {
  AccountClient,
  apiMethodAndKeyNames,
  apiResolver,
  GET_FIELD,
  GET_REQUESTERS_BY_CRITERIA,
  GET_TECHNICIANS_BY_CRITERIA
} from '@nile/graphql';

const DEFAULT_TICKET_CONDITION = {
  joinOperator: 'AND',
  operands: [{ attribute: 'ticketId', operator: 'isNotEmpty', value: '' }]
};

/**
 * For entity type, if we want to call a specif api, this method will be called.
 * @param {object} item
 */
function getQuery(item) {
  let entity = item?.name?.split('.')[0] || '';
  if (entity === 'worklog') {
    const entityName = item?.name?.split('.');
    entity = entityName
      ?.slice(entityName.length - 2, entityName.length - 1)
      .join('');
  }
  switch (entity) {
    case 'technician':
    case 'followers':
    case 'owner':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      return GET_REQUESTERS_BY_CRITERIA;
  }
}

/**
 * After getting values from the API, this value specifies
 * the method name to process the data
 * @param {object} item
 */
function getNameKey(item) {
  let entity = item?.split('.')[0] || '';
  if (entity === 'worklog') {
    // entity = item?.split('.')?.[1];
    const entityName = item?.split('.');
    entity = entityName
      ?.slice(entityName.length - 2, entityName.length - 1)
      .join('');
  }
  switch (entity) {
    case 'technician':
    case 'followers':
    case 'owner':
      return 'getTechnicianList';
    default:
      return 'getRequesterList';
  }
}

function formatOperatorValues(allOperators, operator, intl) {
  return (
    allOperators?.getRuleBuilderOperators?.operator_placeholders?.[
      operator
    ]?.map((el) => {
      return {
        ...el,
        label: getTranslatedText(intl, el.key),
        value: getTranslatedText(intl, el.key),
        name: el.key
      };
    }) || []
  );
}

/**
 * This method gives the query and client using which the API calls will be made
 * after selecting the attribute
 * @param {object} item
 * @param {String} operator
 */

function getPropValues(item, operator, allOperators, intl) {
  if (item?.name) {
    if (item.isCustom) {
      const splitArray = item?.name?.split('.');
      const columnName = splitArray?.[splitArray?.length - 1];
      const placeholderOperators = allOperators?.getRuleBuilderOperators
        ?.operator_placeholders
        ? Object.keys(
            allOperators?.getRuleBuilderOperators?.operator_placeholders
          )
        : [];
      const isPlaceholderOperator = placeholderOperators.indexOf(operator) > -1;
      if (operator && isPlaceholderOperator) {
        return formatOperatorValues(allOperators, operator, intl);
      }
      return AccountClient.query({
        query: GET_FIELD,
        variables: {
          input: {
            columnName: columnName,
            module: ALL_MODULES.ticket
          }
        }
      });
    }
    const query = apiResolver.WorklogEntry[item.name]?.api;
    const client = apiResolver.WorklogEntry[item.name]?.client;
    let variables = apiResolver.WorklogEntry[item.name]?.variables || {};
    if (item.name === 'worklog.client.name') {
      variables = {
        listInfo: {}
      };
    }
    if (query && client) {
      return client.query({
        query: query,
        variables: variables
      });
    }
    if (operator) {
      return formatOperatorValues(allOperators, operator, intl);
    }
    return {
      client: AccountClient,
      query: getQuery(item),
      variables: variables
    };
  }
  return {};
}

async function onWorklogEntrySelect(params) {
  const { item: field, operator, fields, allOperators, intl } = params;
  const allFields = fields;
  const selectedField = allFields.find((el) => el.name === field);
  if (field.split('.')?.length > 1) {
    let ent = field.split('.')[0];
    if (ent === 'worklog') {
      // ent = field.split('.')[1];
      const entityName = field?.split('.');
      ent = entityName
        ?.slice(entityName.length - 2, entityName.length - 1)
        .join('');
    }
    const otherEntities = [
      'techGroup',
      'client',
      'ticket',
      'project',
      'site',
      'serviceItem'
    ];
    if (!otherEntities.includes(ent))
      return getPropValues(selectedField, null, allOperators);
  }
  if (selectedField?.name === 'status') {
    return Object.values(WORKLOG_ITEM_STATE).map((state) => ({
      name: state,
      label: state,
      value: state
    }));
  }
  if (selectedField?.dataType === 'boolean') {
    return [
      {
        name: 'true',
        label: 'true',
        value: true
      },
      {
        name: 'false',
        label: 'false',
        value: false
      }
    ];
  }
  const allValues = await getPropValues(
    selectedField,
    operator,
    allOperators,
    intl
  );
  let res = [];

  if (selectedField?.name) {
    let method = apiMethodAndKeyNames[selectedField.name]?.method;
    const secondaryMethod =
      apiMethodAndKeyNames[selectedField?.name]?.secondaryMethod;
    const key = apiMethodAndKeyNames[selectedField.name]?.key;
    const nestedKey = apiMethodAndKeyNames[selectedField.name]?.nestedKey;

    let dataToMap = [];
    if (secondaryMethod) {
      dataToMap = allValues.data[method]?.[secondaryMethod];
    } else if (operator === 'on') {
      dataToMap = allValues;
    } else {
      dataToMap = allValues.data?.[method];
    }

    if (selectedField.nestedTo) {
      const nestedProp = apiMethodAndKeyNames[selectedField.name]?.nestedKey;
      if (!dataToMap && !method) {
        dataToMap = allValues.data?.getField?.options;
      }
      dataToMap.forEach((el) => {
        if (el[nestedProp]) {
          res = res.concat(
            el[nestedProp].map((c) => {
              return {
                name: c[key],
                value: c[key],
                label: c[key]
              };
            })
          );
        } else {
          res = res.concat([
            { name: el?.value, value: el?.value, label: el?.value }
          ]);
        }
      });
    } else if (operator === 'on') {
      res = allValues;
    } else if (selectedField.isCustom) {
      method = 'getField';
      const fldOptions =
        allValues?.data?.[method]?.options ||
        allValues?.data?.[method]?.fieldOptions;
      res = allValues.data
        ? fldOptions?.map((el) => {
            return {
              name: el?.value,
              value: el?.value,
              label: el?.value
            };
          })
        : [];
    } else if (nestedKey) {
      res = dataToMap?.[nestedKey]
        ? dataToMap?.[nestedKey].map((el) => {
            return {
              name: el[key],
              value: el[key],
              label: el[key]
            };
          })
        : [];
    } else {
      res = allValues.data
        ? dataToMap.map((el) => {
            return {
              name: key ? el[key] : el,
              value: key ? el[key] : el,
              label: key ? el[key] : el
            };
          })
        : [];
    }
  }
  return res;
}

/**
 * This method is called for autocomplete fields.
 * Usually apis will be called when we choose attributes. But for autocomplete,
 * on typing few characters, api will be called. This is the method.
 * @param {object} client
 * @param {gql} query
 * @param {object} params
 */
const onWorklogEntrySearch = async (client, query, params) => {
  let attribute = params?.field?.split('.')[1];
  const entity = params?.field?.split('.')?.[0];
  if (entity === 'worklog') {
    attribute = params?.field?.split('.').pop();
    // const entityName = params?.field?.split('.');
    // attribute = entityName
    //   ?.slice(entityName.length - 2, entityName.length - 1)
    //   .join('');
  }
  const nameKey = getNameKey(params.field);
  const res = await client.query({
    query: query,
    variables: {
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: attribute || 'firstName',
              operator: 'startsWith',
              value: params.inputValue
            }
          ]
        }
      }
    }
  });
  const allOptions = res?.data?.[nameKey]
    ? res.data[nameKey].userList.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
    : [];
  return allOptions;
};

export { onWorklogEntrySelect, onWorklogEntrySearch, DEFAULT_TICKET_CONDITION };
